/**
 * Error attributes that follow the observability charter. `error.kind` and `error.message` are
 * always present; the HTTP and code fields only when the original error carries them.
 */
export type ErrorAttributes = {
  'error.kind': string;
  'error.message': string;
  'http.status_code'?: number;
  'error.code'?: string;
};

/**
 * Extracts observability attributes from any thrown value.
 *
 * It reads `name`, `status`, `response.status` and the codes, and never `constructor.name`: class
 * names are renamed in production builds, so `AxiosError` would report itself as a single letter.
 *
 * `error.code` carries the wire code when there is one, because it is what appears in the backend
 * logs and a tag that cannot be correlated across the two sides diagnoses nothing. The domain code
 * is derived from it and adds no information.
 */
export const extractErrorAttributes = (error: unknown): ErrorAttributes => {
  if (!(error instanceof Error)) {
    return { 'error.kind': 'Unknown', 'error.message': String(error) };
  }

  const status =
    (error as { status?: number }).status ?? (error as { response?: { status?: number } }).response?.status;

  const code = (error as { apiCode?: string }).apiCode ?? (error as { code?: string }).code;

  const kind = error.name && error.name.length > 0 ? error.name : 'Error';

  return {
    'error.kind': kind,
    'error.message': error.message,
    ...(typeof status === 'number' ? { 'http.status_code': status } : {}),
    ...(code ? { 'error.code': code } : {}),
  };
};
